// SortDropdown — the shared sort control: an outlined "Sort · <active key>"
// trigger, a menu of sort keys with a check on the active one, and a footer row
// that flips the direction independently of the key. Only the open state lives
// here; the sort value is the caller's, so the same state can drive a
// DataTable's sortable headers.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from './Dropdown';
import Icon from './Icon';

export type SortDropdownDirection = 'asc' | 'desc';

export interface SortOption<K extends string = string> {
  key: K;
  label: string;
}

export interface SortValue<K extends string = string> {
  key: K;
  type: SortDropdownDirection;
}

/** Generic over the key union (e.g. a backend sort-key type), so `onChange`
 *  hands back the caller's own key type without a cast. */
export interface SortDropdownProps<K extends string = string> {
  options: SortOption<K>[];
  value: SortValue<K>;
  onChange: (next: SortValue<K>) => void;
  /** Whole trigger text. Default t('sort_prefix') → "Sort · {{value}}". */
  label?: string;
  /** Drop the ascending/descending footer row. Default false. */
  hideDirection?: boolean;
  /** Panel edge to align with the trigger. Default 'start' (toolbar lead). */
  align?: 'start' | 'end';
  width?: number;
  /** Render the panel in a body portal to escape a clipping ancestor. */
  portal?: boolean;
}

const SortDropdown = <K extends string = string>({
  options,
  value,
  onChange,
  label,
  hideDirection = false,
  align = 'start',
  width = 220,
  portal = false,
}: SortDropdownProps<K>) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Fall back to the raw key so a value outside `options` still reads sanely.
  const activeLabel = options.find((o) => o.key === value.key)?.label ?? value.key;
  const ascending = value.type === 'asc';

  // Picking a key keeps the direction — flipping it is the footer row's job.
  const pick = (key: K) => {
    onChange({ key, type: value.type });
    setOpen(false);
  };

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align={align}
      width={width}
      portal={portal}
      className='sort-dropdown'
      trigger={
        <button
          type='button'
          className={`sort-dropdown__trigger${open ? ' is-open' : ''}`}
          aria-haspopup='menu'
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name='arrow-up-down' size={16} />
          {label ?? t('sort_prefix', { value: activeLabel })}
          <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
        </button>
      }
    >
      {options.map((option) => {
        const active = option.key === value.key;
        return (
          <button
            key={option.key}
            type='button'
            className='app-dropdown__item'
            role='menuitemradio'
            aria-checked={active}
            onClick={() => pick(option.key)}
          >
            <span className='sort-dropdown__label'>{option.label}</span>
            {active && <Icon name='check' size={16} className='sort-dropdown__check' />}
          </button>
        );
      })}

      {/* Direction is a toggle, not a pick — the panel stays open. */}
      {!hideDirection && (
        <>
          <div className='app-dropdown__divider' role='separator' />
          <button
            type='button'
            className='app-dropdown__item sort-dropdown__direction'
            role='menuitem'
            onClick={() => onChange({ key: value.key, type: ascending ? 'desc' : 'asc' })}
          >
            <Icon name={ascending ? 'arrow-up' : 'arrow-down'} size={16} />
            {ascending ? t('sort_ascending') : t('sort_descending')}
          </button>
        </>
      )}
    </Dropdown>
  );
};

export default SortDropdown;
